import { CheckCircle2, FolderSync, Loader2, Pencil, Play, Plus, Trash2, TriangleAlert, XCircle } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { ApiRequestError, useApi } from "../api/client";
import { useCredentials, useInvalidateOrg, useSession, useShares } from "../api/queries";
import type { Credential, Job, Share, ShareInput, Snapshot } from "../api/types";
import { common } from "../i18n/common";
import { useT } from "../i18n/core";
import { useFormat } from "../lib/format";
import { ErrorState, LoadingRows } from "../ui/Common";
import { Dialog } from "../ui/Dialog";
import { Pill } from "../ui/Pills";
import { useToast } from "../ui/Toasts";
import { machineSharesMessages } from "./machineShares.i18n";

/** Aba de compartilhamentos SMB da máquina (ADR 0030): cadastro, teste com checklist, backup na hora e pontos de restauração. */
export function MachineShares({ orgId, machineId, revoked, snapshots, jobs }: {
  orgId: string; machineId: string; revoked: boolean; snapshots: Snapshot[];
  /** Atividade da máquina: a mensagem do teste em curso é o checklist ao vivo. */
  jobs: Job[];
}) {
  const api = useApi();
  const toast = useToast();
  const invalidate = useInvalidateOrg();
  const session = useSession();
  // Só administrador enxerga o catálogo de credenciais; os demais veem "com credencial".
  const admin = session.data?.user.role === "org_admin" || session.data?.user.platformAdmin === true;
  const shares = useShares(orgId, machineId);
  const creds = useCredentials(orgId, admin);
  const t = useT(machineSharesMessages);
  const tc = useT(common);
  const f = useFormat();
  const [editing, setEditing] = useState<Share | "new" | null>(null);
  const [removing, setRemoving] = useState<Share | null>(null);
  const [busy, setBusy] = useState(false);

  if (shares.isPending) return <LoadingRows rows={4} />;
  if (shares.isError) return <div className="panel-body"><ErrorState error={shares.error} /></div>;
  const list = shares.data;
  const credName = (id: string) => creds.data?.find((c) => c.id === id)?.name;

  async function run(sh: Share, kind: "test" | "backup") {
    setBusy(true);
    try {
      if (kind === "test") await api.testShare(orgId, machineId, sh.id);
      else await api.startShareBackup(orgId, machineId, sh.id);
      toast("ok", kind === "test" ? t.testSent(sh.name) : t.backupSent(sh.name));
      invalidate(orgId);
    } catch (e) {
      toast("error", e instanceof ApiRequestError ? e.message : t.actionFailed);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!removing) return;
    setBusy(true);
    try {
      await api.deleteShare(orgId, machineId, removing.id);
      toast("ok", t.removed(removing.name));
      setRemoving(null);
      invalidate(orgId);
    } catch (e) {
      toast("error", e instanceof ApiRequestError ? e.message : t.removeFailed);
    } finally {
      setBusy(false);
    }
  }

  function pointsTable(points: Snapshot[]) {
    if (points.length === 0) return <p className="muted" style={{ fontSize: "var(--fs-13)" }}>{t.noPoints}</p>;
    return (
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>{t.colWhen}</th>
              <th className="right">{t.colFiles}</th>
              <th className="right">{t.colSize}</th>
              <th><span className="sr-only">{t.colActions}</span></th>
            </tr>
          </thead>
          <tbody>
            {points.map((s) => (
              <tr key={s.id}>
                <td>
                  <div className="host">
                    <strong style={{ fontFamily: "var(--font-ui)", fontWeight: 600 }}>{t.when(f.weekdayDate(s.startedAt), f.time(s.startedAt))}</strong>
                    <span className="mono">{s.id.slice(0, 12)} · {s.trigger === "manual" ? t.triggerManual : t.triggerSchedule}</span>
                  </div>
                </td>
                <td className="right num">{t.files(s.fileCount)}</td>
                <td className="right num">{f.bytes(s.totalBytes)}</td>
                <td className="right">
                  {/* Mesmo destino da aba Snapshots: o assistente de restauração já sabe lidar com a árvore. */}
                  <Link className="btn btn-sm" to={`/o/${orgId}/machines/${encodeURIComponent(machineId)}/restore?snapshot=${s.id}`}>{t.restore}</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="panel-body" style={{ display: "grid", gap: 20 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap" }}>
        <p className="ink-2" style={{ fontSize: "var(--fs-13)", maxWidth: "62ch" }}>{t.intro}</p>
        {!revoked && (
          <button className="btn" type="button" onClick={() => setEditing("new")}>
            <Plus aria-hidden="true" /> {t.add}
          </button>
        )}
      </div>

      {list.length === 0 && <div className="empty"><FolderSync aria-hidden="true" />{t.none}</div>}

      {list.map((sh) => {
        const points = snapshots.filter((s) => s.shareId === sh.id);
        const cred = sh.credentialId ? (credName(sh.credentialId) ? t.credentialNamed(credName(sh.credentialId) ?? "") : t.credentialSome) : t.credentialNone;
        const sched = sh.schedule ? (sh.schedule.frequency === "hourly" ? t.scheduleHourly : t.scheduleOwn(sh.schedule.time)) : t.scheduleFromPolicy;
        const lt = sh.lastTest;
        const live = sh.activeJob ? jobs.find((j) => j.shareId === sh.id && (j.status === "running" || j.status === "queued")) : undefined;
        return (
          <section key={sh.id} aria-labelledby={`share-${sh.id}`} style={{ display: "grid", gap: 12, borderTop: "1px solid var(--line)", paddingTop: 16 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap" }}>
              <div style={{ minWidth: 0, display: "grid", gap: 4 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <h3 id={`share-${sh.id}`} style={{ fontSize: "var(--fs-15, 0.95rem)" }}>{sh.name}</h3>
                  {!sh.enabled && <span className="tag" title={t.disabledHint}>{t.disabled}</span>}
                </div>
                <span className="mono ink-2" style={{ fontSize: "var(--fs-12)", overflowWrap: "anywhere" }}>{sh.unc}</span>
                <span className="ink-2" style={{ fontSize: "var(--fs-12)" }}>{[cred, sched].join(" · ")}</span>
              </div>
              {!revoked && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button className="btn btn-sm" type="button" disabled={busy || sh.activeJob !== ""} onClick={() => run(sh, "test")}>
                    <CheckCircle2 aria-hidden="true" /> {sh.activeJob === "sharetest" ? t.testing : t.test}
                  </button>
                  <button className="btn btn-sm" type="button" disabled={busy || sh.activeJob !== ""} onClick={() => run(sh, "backup")}>
                    <Play aria-hidden="true" /> {sh.activeJob === "share" ? t.backingUp : t.backupNow}
                  </button>
                  <button className="btn btn-sm" type="button" onClick={() => setEditing(sh)} aria-label={`${t.edit}: ${sh.name}`}>
                    <Pencil aria-hidden="true" /> {t.edit}
                  </button>
                  <button className="btn btn-sm" type="button" onClick={() => setRemoving(sh)} aria-label={`${t.remove}: ${sh.name}`}>
                    <Trash2 aria-hidden="true" /> {t.remove}
                  </button>
                </div>
              )}
            </div>

            {live && (
              <div className="callout" role="status" aria-live="polite">
                <Loader2 aria-hidden="true" className="spin" />
                <span><strong>{sh.activeJob === "sharetest" ? t.testing : t.backingUp}</strong> {live.message}</span>
              </div>
            )}

            {lt ? (
              <div className={`callout ${lt.ok ? (lt.warnings.length ? "warn" : "") : "crit"}`} role="status">
                {lt.ok ? (lt.warnings.length ? <TriangleAlert aria-hidden="true" /> : <CheckCircle2 aria-hidden="true" />) : <XCircle aria-hidden="true" />}
                <div style={{ display: "grid", gap: 4, minWidth: 0 }}>
                  <span><strong>{lt.ok ? t.testOk(f.ago(lt.at)) : t.testFailed(f.ago(lt.at))}.</strong> {lt.ok ? "" : lt.message}</span>
                  {lt.ok && (
                    <span className="mono" style={{ fontSize: "var(--fs-12)" }}>
                      {[t.testInfo(lt.dialect, f.int(lt.entries), f.bytes(lt.bytes)), lt.encrypted ? t.encrypted : lt.signed ? t.signed : ""].filter(Boolean).join(" · ")}
                    </span>
                  )}
                  {/* Checklist com o resultado de cada passo; a lista solta de avisos é para agente antigo. */}
                  {lt.steps.length > 0 ? (
                    <ul aria-label={t.testSteps} style={{ listStyle: "none", padding: 0, margin: "4px 0 0", display: "grid", gap: 2, fontSize: "var(--fs-12)" }}>
                      {lt.steps.map((st, i) => (
                        <li key={`${st.code}-${i}`} style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
                          {st.status === "ok" ? <CheckCircle2 aria-label={t.stepStatus.ok} style={{ width: 14, height: 14, flex: "none", marginTop: 1, color: "var(--ok-mark)" }} />
                            : st.status === "warn" ? <TriangleAlert aria-label={t.stepStatus.warn} style={{ width: 14, height: 14, flex: "none", marginTop: 1, color: "var(--warn-mark)" }} />
                            : <XCircle aria-label={t.stepStatus.fail} style={{ width: 14, height: 14, flex: "none", marginTop: 1, color: "var(--crit-mark)" }} />}
                          <span style={{ overflowWrap: "anywhere" }}>{st.message}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                  {lt.warnings.map((w) => <span key={w.code + w.message} style={{ fontSize: "var(--fs-12)" }}>{w.message}</span>)}
                </div>
              </div>
            ) : !live ? (
              <div><Pill tone="idle">{t.neverTested}</Pill></div>
            ) : null}

            <h4 style={{ fontSize: "var(--fs-13)", margin: 0 }}>{t.points}</h4>
            {pointsTable(points)}
          </section>
        );
      })}

      {editing && (
        <ShareDialog orgId={orgId} machineId={machineId} share={editing === "new" ? null : editing} credentials={admin ? (creds.data ?? []) : null}
          onClose={(changed) => { setEditing(null); if (changed) invalidate(orgId); }} />
      )}

      <Dialog
        open={removing !== null}
        onOpenChange={(v) => { if (!v) setRemoving(null); }}
        title={removing ? t.removeTitle(removing.name) : ""}
        description={t.removeDescription}
        footer={
          <>
            <button className="btn" type="button" onClick={() => setRemoving(null)}>{tc.cancel}</button>
            <button className="btn btn-primary" style={{ background: "var(--crit-mark)", borderColor: "var(--crit-mark)" }} type="button" onClick={remove} disabled={busy}>{t.remove}</button>
          </>
        }
      />
    </div>
  );
}

/** Cadastro ou edição. credentials nulo: quem está logado não lista o catálogo, e a credencial atual fica só exibida. */
function ShareDialog({ orgId, machineId, share, credentials, onClose }: {
  orgId: string; machineId: string; share: Share | null; credentials: Credential[] | null; onClose: (changed: boolean) => void;
}) {
  const api = useApi();
  const toast = useToast();
  const t = useT(machineSharesMessages);
  const tc = useT(common);

  const [name, setName] = useState(share?.name ?? "");
  const [host, setHost] = useState(share?.host ?? "");
  const [shareName, setShareName] = useState(share?.share ?? "");
  const [path, setPath] = useState(share?.path ?? "");
  const [credentialId, setCredentialId] = useState(share?.credentialId ?? "");
  const [exclude, setExclude] = useState((share?.exclude ?? []).join("\n"));
  const [ownSchedule, setOwnSchedule] = useState(Boolean(share?.schedule));
  const [time, setTime] = useState(share?.schedule?.time ?? "22:00");
  const [windowHours, setWindowHours] = useState(share?.schedule?.windowHours ?? 6);
  const [weekdays, setWeekdays] = useState<number[]>(share?.schedule?.weekdays ?? [0, 1, 2, 3, 4, 5, 6]);
  const [enabled, setEnabled] = useState(share?.enabled ?? true);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  const errors = {
    name: !name.trim() ? t.nameRequired : null,
    host: !host.trim() ? t.hostRequired : null,
    share: !shareName.trim() ? t.shareRequired : null,
  };
  const show = (k: keyof typeof errors) => (touched ? errors[k] : null);

  async function save(ev: React.FormEvent) {
    ev.preventDefault();
    setTouched(true);
    if (Object.values(errors).some(Boolean)) return;
    const input: ShareInput = {
      name: name.trim(), host: host.trim(), share: shareName.trim(), path: path.trim(), credentialId, enabled,
      exclude: exclude.split("\n").map((x) => x.trim()).filter(Boolean),
      schedule: ownSchedule ? { frequency: "daily", time, weekdays: [...weekdays].sort(), windowHours: Math.min(24, Math.max(1, windowHours)) } : null,
    };
    setBusy(true);
    try {
      const saved = share ? await api.updateShare(orgId, machineId, share.id, input) : await api.createShare(orgId, machineId, input);
      toast("ok", share ? t.saved(saved.name) : t.created(saved.name));
      onClose(true);
    } catch (err) {
      toast("error", err instanceof ApiRequestError ? err.message : t.saveFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => { if (!v) onClose(false); }}
      title={share ? t.editTitle(share.name) : t.createTitle}
      footer={
        <>
          <button className="btn" type="button" onClick={() => onClose(false)}>{tc.cancel}</button>
          <button className="btn btn-primary" type="submit" form="share-form" disabled={busy}>{share ? t.save : t.create}</button>
        </>
      }
    >
      <form id="share-form" onSubmit={save} style={{ display: "grid", gap: 16 }} noValidate>
        <div className="field">
          <label htmlFor="share-nome">{t.fieldName}</label>
          <input id="share-nome" className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder={t.namePlaceholder} aria-invalid={Boolean(show("name"))} />
          {show("name") && <span className="error">{show("name")}</span>}
        </div>
        <div className="form-grid">
          <div className="field">
            <label htmlFor="share-host">{t.fieldHost}</label>
            <input id="share-host" className="input mono" value={host} onChange={(e) => setHost(e.target.value)} placeholder="nas01.empresa.local" autoComplete="off" spellCheck={false} aria-invalid={Boolean(show("host"))} />
            {show("host") ? <span className="error">{show("host")}</span> : <span className="hint">{t.hostHint}</span>}
          </div>
          <div className="field">
            <label htmlFor="share-share">{t.fieldShare}</label>
            <input id="share-share" className="input mono" value={shareName} onChange={(e) => setShareName(e.target.value)} placeholder="documentos" autoComplete="off" spellCheck={false} aria-invalid={Boolean(show("share"))} />
            {show("share") ? <span className="error">{show("share")}</span> : <span className="hint">{t.shareHint}</span>}
          </div>
        </div>
        <div className="field">
          <label htmlFor="share-path">{t.fieldPath}</label>
          <input id="share-path" className="input mono" value={path} onChange={(e) => setPath(e.target.value)} placeholder="Departamentos/Financeiro" autoComplete="off" spellCheck={false} />
          <span className="hint">{t.pathHint}</span>
        </div>
        <div className="field">
          <label htmlFor="share-cred">{t.fieldCredential}</label>
          {credentials ? (
            <select id="share-cred" className="select" value={credentialId} onChange={(e) => setCredentialId(e.target.value)}>
              <option value="">{t.credentialNoneOption}</option>
              {credentials.map((c) => <option key={c.id} value={c.id}>{c.name}{c.username ? ` (${c.domain ? `${c.domain}\\` : ""}${c.username})` : ""}</option>)}
            </select>
          ) : (
            <input id="share-cred" className="input mono" value={credentialId || t.credentialNoneOption} disabled readOnly />
          )}
          <span className="hint">{credentials ? t.credentialHint : t.credentialReadOnly}</span>
        </div>
        <div className="field">
          <label htmlFor="share-exclude">{t.fieldExclude}</label>
          <textarea id="share-exclude" className="input mono" rows={3} value={exclude} onChange={(e) => setExclude(e.target.value)} spellCheck={false} />
          <span className="hint">{t.excludeHint}</span>
        </div>

        <div className="field">
          <span className="label" id="share-agenda">{t.fieldSchedule}</span>
          <div className="segmented" role="group" aria-labelledby="share-agenda">
            <button type="button" aria-pressed={!ownSchedule} onClick={() => setOwnSchedule(false)}>{t.schedulePolicy}</button>
            <button type="button" aria-pressed={ownSchedule} onClick={() => setOwnSchedule(true)}>{t.scheduleCustom}</button>
          </div>
        </div>
        {ownSchedule && (
          <>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="share-hora">{t.fieldTime}</label>
                <input id="share-hora" className="input num" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="share-janela">{t.fieldWindow}</label>
                <input id="share-janela" className="input num" type="number" min={1} max={24} value={windowHours} onChange={(e) => setWindowHours(Number(e.target.value))} />
              </div>
            </div>
            <div className="field">
              <span className="label" id="share-dias">{t.fieldDays}</span>
              <div className="segmented" role="group" aria-labelledby="share-dias">
                {tc.weekdaysShort.map((d, i) => {
                  const on = weekdays.includes(i);
                  return <button key={d} type="button" aria-pressed={on} onClick={() => setWeekdays(on ? weekdays.filter((x) => x !== i) : [...weekdays, i])}>{d}</button>;
                })}
              </div>
            </div>
          </>
        )}

        <label className="check" htmlFor="share-ligado">
          <input id="share-ligado" type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span><strong>{t.enabled}</strong><br /><span className="ink-2">{t.disabledHint}</span></span>
        </label>
      </form>
    </Dialog>
  );
}
